import React, {
  CSSProperties,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import { BarSeriesType } from "@mui/x-charts";

import { Bucket } from "../../../../../api/indicators";
import { DashboardFiltersContext } from "../../filters/DashboardFiltersContext";

import LineBarChartSkeleton from "../areaChart/AreaChartSkeleton";
import LineBarChartError from "../lineBarChart/LineBarChartError";
import ChartNoData from "../ChartNoData";


import { formatValue, getUniqueProperties } from "../../../../../helpers/helpers";
import { UserContext } from "../../../../../context/UserContext";
import DoubleBarChartBase from "./DoubleBarChartBase";

export interface LineBarChartDataItem {
  dateTime: Date;
  barData: number;
  [key: string]: number | Date;
}

export interface DoubleBarChartProps {
  title: string;
  currency?: string;
  description: string;
  textLink?: string;
  linkChart?: string;
  isLoading: boolean;
  isError: boolean;
  leftAxisLabel: string;
  rightAxisLabel?: string;
  data: LineBarChartDataItem[];
  bucket: Bucket;
  setBucket: (bucket: Bucket) => void;
  height: number;
}

const selectStyle: CSSProperties = {
  fontFamily: "Open Sans",
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "24px",
  width: "210px",
};

const DoubleBarChart = ({
  title,
  currency,
  description,
  textLink,
  linkChart,
  isLoading,
  isError,
  leftAxisLabel,
  rightAxisLabel,
  data,
  bucket,
  setBucket,
  height,
}: DoubleBarChartProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { locale } = useContext(UserContext);
  const { openFilters } = useContext(DashboardFiltersContext);

  const [series, setSeries] = useState<BarSeriesType[]>([]);

  const colours = useMemo(
    () => [
      theme.palette.blue.main,
      theme.palette.cyan.main,
      theme.palette.teal.main,
      theme.palette.lightGreen.main,
      theme.palette.lime.main,
      theme.palette.yellow.main,
      theme.palette.orange.main,
      theme.palette.deepOrange.main,
      theme.palette.fucsia.main,
      theme.palette.purple.main,
    ],
    [theme]
  );

  const noData = !data?.length;


  useEffect(() => {
    if (noData) return;

    const tenderKeys = getUniqueProperties(data, [
      "dateTime",
      "barData",
    ]);

    const tenderSeries: BarSeriesType[] = tenderKeys.map(
      (key, index) => ({
        id: key,
        label: key,
        type: "bar",
        dataKey: key,
        stack: "tenders",
        yAxisId: "y-axis-left-id",
        color: colours[index % colours.length],
        valueFormatter: (v) =>
          formatValue(v as number, true, currency, locale),
      })
    );


    const volumeSeries: BarSeriesType = {
      id: "volume",
      label: rightAxisLabel,
      type: "bar",
      dataKey: "barData",
      yAxisId: "y-axis-right-id",
      color: theme.palette.darkerGrey.light,
    };

    setSeries([...tenderSeries, volumeSeries]);
  }, [
    data,
    colours,
    currency,
    locale,
    rightAxisLabel,
    theme.palette.darkerGrey.light,
    noData,
  ]);

  const sanitizedData = useMemo(() =>
    data.map((item) => {
      const sanitizedItem: LineBarChartDataItem = {
        ...item,
      };

      Object.keys(sanitizedItem).forEach((key) => {
        const value = sanitizedItem[key];

        if (
          key !== "dateTime" &&
          typeof value === "number" &&
          value < 0
        ) {
          sanitizedItem[key] = 0;
        }
      });

      return sanitizedItem;
    })
    , [data]);

  return (
    <Card
      elevation={0}
      sx={{
        border: "solid 1px",
        borderColor: theme.palette.grey["12p"],
        marginX: "8px",
        padding: "12px 16px",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        breakInside: "avoid",
        pageBreakInside: "avoid",
      }}
    >
      {isLoading && <LineBarChartSkeleton />}

      {!isLoading && (
        <>
          {isError && (
            <LineBarChartError
              title={title}
              description={description}
              bucket={bucket}
              setBucket={setBucket}
            />
          )}

          {!isError && noData && (
            <ChartNoData
              height={height}
              title={title}
              description={description}
              openFilters={openFilters}
            />
          )}

          {!isError && !noData && (
            <Box sx={{ minHeight: height }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  marginY: "6px",
                }}
              >
                <Box>
                  <Typography variant="h2">{title}</Typography>
                  <Typography variant="body2">
                    {description}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <TextField
                    select
                    sx={selectStyle}
                    label={t(
                      "screens.dashboarding.charts.showBy"
                    )}
                    value={bucket}
                    onChange={(e) =>
                      setBucket(e.target.value as Bucket)
                    }
                  >
                    <MenuItem value="hour">
                      {t("components.select.hour")}
                    </MenuItem>
                    <MenuItem value="day">
                      {t("components.select.days")}
                    </MenuItem>
                    <MenuItem value="week">
                      {t("components.select.week")}
                    </MenuItem>
                  </TextField>
                </Box>
              </Box>

              <DoubleBarChartBase
                bucket={bucket}
                series={series}
                data={sanitizedData}
                height={height}
                leftAxisLabel={leftAxisLabel}
                rightAxisLabel={rightAxisLabel}
                currency={currency}
              />

              {!!linkChart && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "auto",
                  }}
                >
                  <Button
                    href={linkChart}
                    sx={{ color: "primary.main" }}
                    style={theme.typography.pieBarText}
                  >
                    {textLink}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Card>
  );
};

export default DoubleBarChart;
